import React, { useState, useEffect } from 'react';
import { IonPage, IonContent, IonModal } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { useHomeLayoutStore } from '../../store/homeLayoutStore';
import { useCalendarStore, CalendarIntegration } from '../../store/calendarStore';
import { api } from '../../services/api';
import Icon from '../../components/Icon';
import FeatureMobileHeader from '../../components/FeatureMobileHeader';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { showToast } from '../../components/Toast';
import { MEMBER_COLOURS, initials } from '../../utils/constants';
import { WORLD_CURRENCIES } from '../../data/currencies';
import { fetchExchangeRates, guessLocaleCurrency } from '../../utils/currency';
import { openExternal } from '../../utils/openExternal';
import {
  isDeviceCalendarAvailable,
  getPlatform as getDeviceCalendarPlatform,
  readDeviceCalendarEvents,
} from '../../services/deviceCalendar';

/* ── Bottom Sheet ── */
interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const BottomSheet: React.FC<SheetProps> = ({ open, onClose, title, children }) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (visible) {
      setClosing(true);
      const timer = setTimeout(() => { setVisible(false); setClosing(false); document.body.style.overflow = ''; }, 250);
      return () => clearTimeout(timer);
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => { onClose(); }, 250);
  };

  if (!visible) return null;
  return (
    <div
      className={closing ? 'fh-sheet-backdrop--closing' : 'fh-sheet-backdrop'}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center',
        padding: 'calc(env(safe-area-inset-top, 0px) + 48px) 16px 24px',
        overflowY: 'auto',
        touchAction: 'none',
        overscrollBehavior: 'none',
      }}
      onClick={handleClose}
      onTouchMove={e => { if (e.target === e.currentTarget) e.preventDefault(); }}
    >
      <div
        className={closing ? 'fh-sheet-content--closing' : 'fh-sheet-content'}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 500, background: 'var(--fh-dark-card)',
          borderRadius: 16,
          padding: 20,
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
        }}
      >
        {/* iOS-style drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <div style={{ width: 36, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.15)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontSize: 18, fontWeight: 800, color: 'var(--fh-dark-text)',
          }}>{title}</h2>
          <button onClick={handleClose} style={{ background: 'var(--fh-overlay-medium)', border: 'none', borderRadius: 10, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Icon name="close" size={18} color="var(--fh-dark-text-secondary)" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ── Main Page ── */
const MorePage: React.FC = () => {
  const { user, family, logout, refreshFamily } = useAuthStore();
  const isAdmin = family?.role === 'admin';
  const isDesktop = useIsDesktop();
  const { mode: themeMode, setMode: setThemeMode } = useThemeStore();
  const { tiles, toggleTile } = useHomeLayoutStore();
  const history = useHistory();
  const [, forceNotifUpdate] = useState(0);
  // Sheet states — all close on unmount to prevent stale sheets on re-nav
  const [membersOpen, setMembersOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [calendarSyncOpen, setCalendarSyncOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('fh-notif-prefs');
      return saved ? JSON.parse(saved) : { morning: true, school: true, geofence: true, budget: true, mentions: false, quiet: true };
    } catch { return { morning: true, school: true, geofence: true, budget: true, mentions: false, quiet: true }; }
  });
  const toggleNotif = (k: string) => {
    const next = { ...notifPrefs, [k]: !notifPrefs[k] };
    setNotifPrefs(next);
    localStorage.setItem('fh-notif-prefs', JSON.stringify(next));
    // Persist to server so push delivery respects preferences
    api.put('/auth/preferences', { preferences: { notifications: next } }).catch(() => {
      /* offline or server issue — localStorage is the source of truth until next sync */
    });
  };

  // Close all sheets when navigating away
  useEffect(() => {
    return () => {
      setMembersOpen(false); setInviteOpen(false); setEditNameOpen(false);
      setAppearanceOpen(false); setCurrencyOpen(false);
      setDashboardOpen(false); setCalendarSyncOpen(false);
      document.body.style.overflow = '';
    };
  }, []);

  // Edit family name state
  const [familyName, setFamilyName] = useState(family?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState(false);

  // Invite code copy state
  const [copied, setCopied] = useState(false);

  // Currency state — uses shared currency data
  const CURRENCIES = WORLD_CURRENCIES.map(c => ({ code: c.code, symbol: c.symbol, label: c.name }));
  const [currencySearch, setCurrencySearch] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    const saved = localStorage.getItem('fh-currency') || localStorage.getItem('fh-budget-currency');
    if (saved) return saved;
    const backendCurrency = user?.preferences?.currency;
    if (backendCurrency) return backendCurrency;
    return guessLocaleCurrency();
  });
  const activeCurrency = CURRENCIES.find(c => c.code === selectedCurrency) || CURRENCIES[0];


  const handleLogout = () => {
    logout();
    history.replace('/login');
  };

  // ── Delete account (buried) ──
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete('/auth/account');
      logout();
      history.replace('/');
    } catch (err: any) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyInvite = () => {
    if (family?.inviteCode) {
      const message = `Join our family "${family.name}" on FamHearth!\n\nInvite code: ${family.inviteCode}\n\nSign up here: https://famhearth.com/register\n\nChoose "Join with code" and enter the code above.`;
      navigator.clipboard?.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareInvite = async () => {
    if (!family?.inviteCode) return;
    const shareData = {
      title: `Join ${family.name} on FamHearth`,
      text: `Join our family "${family.name}" on FamHearth!\n\nInvite code: ${family.inviteCode}\n\nSign up here: https://famhearth.com/register\n\nChoose "Join with code" and enter the code above.`,
      url: 'https://famhearth.com/register',
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (_) { /* user cancelled */ }
    } else {
      handleCopyInvite();
    }
  };

  const handleSaveFamilyName = async () => {
    if (!familyName.trim()) return;
    setSavingName(true);
    setNameError('');
    setNameSuccess(false);
    try {
      await api.put('/family/update', { name: familyName.trim() });
      await refreshFamily();
      setNameSuccess(true);
      setTimeout(() => {
        setEditNameOpen(false);
        setNameSuccess(false);
      }, 1000);
    } catch (err: any) {
      setNameError(err.response?.data?.error || 'Failed to update family name');
    } finally {
      setSavingName(false);
    }
  };


  useEffect(() => {
    if (editNameOpen) {
      setFamilyName(family?.name || '');
      setNameError('');
      setNameSuccess(false);
    }
  }, [editNameOpen]);

  // ── Connected Calendars ──
  const location = useLocation();
  const {
    integrations: calIntegrations,
    googleConfigured,
    fetchIntegrations,
    startGoogleConnect,
    disconnectIntegration,
    syncGoogle,
    importNativeEvents,
  } = useCalendarStore();
  const [calBusy, setCalBusy] = useState<string | null>(null);

  useEffect(() => {
    // Load integrations on mount so the "X connected" counter is accurate.
    fetchIntegrations().catch(err => console.warn('[MorePage] fetchIntegrations failed:', err));
  }, [fetchIntegrations]);

  // Handle the post-OAuth redirect: `?calendar_sync=connected&provider=google`
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('calendar_sync');
    if (!status) return;
    if (status === 'connected') {
      const provider = params.get('provider') || 'calendar';
      showToast(`${provider === 'google' ? 'Google' : 'Calendar'} connected`, 'success');
      fetchIntegrations().catch(err => console.warn('[MorePage] fetchIntegrations failed:', err));
      setCalendarSyncOpen(true);
    } else if (status === 'error') {
      showToast(`Couldn't connect calendar: ${params.get('reason') || 'unknown'}`, 'error');
    }
    // Clean the URL so we don't re-trigger on every render.
    const clean = location.pathname;
    history.replace(clean);
  }, [location.search, location.pathname, history, fetchIntegrations]);

  const googleIntegration = calIntegrations.find(i => i.provider === 'google');
  const deviceIntegration = calIntegrations.find(
    i => i.provider === getDeviceCalendarPlatform(),
  );

  const handleConnectGoogle = async () => {
    try {
      setCalBusy('google-connect');
      const url = await startGoogleConnect('/app/more');
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url, windowName: '_system' });
      } else {
        window.location.href = url;
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Could not start Google connect', 'error');
    } finally {
      setCalBusy(null);
    }
  };

  const handleSyncGoogle = async (integration: CalendarIntegration) => {
    try {
      setCalBusy(`sync-${integration.id}`);
      const res = await syncGoogle(integration.id);
      showToast(`Google Calendar synced — ${res.inserted + res.updated} events`, 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Sync failed', 'error');
    } finally {
      setCalBusy(null);
    }
  };

  const handleImportDevice = async () => {
    const platform = getDeviceCalendarPlatform();
    if (!platform) return;
    try {
      setCalBusy('device-import');
      const events = await readDeviceCalendarEvents();
      if (!events) {
        showToast('Calendar access denied or unavailable', 'error');
        return;
      }
      if (events.length === 0) {
        showToast('No events found on this device', 'info');
        return;
      }
      const res = await importNativeEvents(platform, events, `${platform === 'ios' ? 'iPhone' : 'Android'} Calendar`);
      showToast(`Imported ${res.inserted + res.updated} events from your phone`, 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Device import failed', 'error');
    } finally {
      setCalBusy(null);
    }
  };

  const handleDisconnect = async (integration: CalendarIntegration, purge: boolean) => {
    const providerLabel = integration.provider === 'google' ? 'Google Calendar'
      : integration.provider === 'ios' ? 'iPhone Calendar' : 'Android Calendar';
    const msg = purge
      ? `Disconnect ${providerLabel} and remove all events it has added?`
      : `Disconnect ${providerLabel}? Previously imported events will remain.`;
    if (!window.confirm(msg)) return;
    try {
      setCalBusy(`disc-${integration.id}`);
      await disconnectIntegration(integration.id, purge);
      showToast(`${providerLabel} disconnected`, 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Disconnect failed', 'error');
    } finally {
      setCalBusy(null);
    }
  };

  const members = family?.members || [];

  const themeLabel = themeMode === 'dark' ? 'Dark' : themeMode === 'light' ? 'Light' : 'System';
  const [clockFormat, setClockFormat] = useState(() => localStorage.getItem('fh-clock-format') || '24h');
  const toggleClockFormat = () => {
    const next = clockFormat === '24h' ? '12h' : '24h';
    setClockFormat(next);
    localStorage.setItem('fh-clock-format', next);
  };

  interface SettingsItem {
    icon: string;
    label: string;
    sub?: string;
    action?: () => void;
    iconColor: string;
    danger?: boolean;
  }

  const familySection: SettingsItem[] = [
    { icon: 'people', label: 'Family Members', sub: `${members.length} member${members.length !== 1 ? 's' : ''}`, action: () => setMembersOpen(true), iconColor: '#aed581' },
    ...(isAdmin ? [
      { icon: 'link', label: 'Invite Code', sub: family?.inviteCode || 'Set up family first', action: () => family ? setInviteOpen(true) : history.push('/family-setup'), iconColor: '#80cbc4' } as SettingsItem,
      { icon: 'edit', label: 'Edit Family Name', sub: family?.name || 'Not set', action: () => family ? setEditNameOpen(true) : history.push('/family-setup'), iconColor: '#b39ddb' } as SettingsItem,
    ] : []),
  ];

  const preferencesSection: SettingsItem[] = [
    { icon: 'palette', label: 'Appearance', sub: `${themeLabel} theme`, action: () => setAppearanceOpen(true), iconColor: '#f48fb1' },
    { icon: 'toggle_on', label: 'Dashboard Features', sub: `${tiles.filter(t => t.visible).length} of ${tiles.length} active`, action: () => setDashboardOpen(true), iconColor: '#64b5f6' },
    { icon: 'dashboard_customize', label: 'Customise Tiles', sub: 'Pick and reorder home tiles', action: () => history.push('/app/customise-home'), iconColor: '#80cbc4' },
    { icon: 'schedule', label: 'Clock Format', sub: clockFormat === '24h' ? '24-hour' : '12-hour', action: toggleClockFormat, iconColor: '#ffb74d' },
    ...(isAdmin ? [{ icon: 'currency_pound', label: 'Currency', sub: `${activeCurrency.code} (${activeCurrency.symbol})`, action: () => setCurrencyOpen(true), iconColor: '#81c784' } as SettingsItem] : []),
  ];

  const integrationsSection: SettingsItem[] = [
    {
      icon: 'sync',
      label: 'Connected Calendars',
      sub: calIntegrations.length > 0
        ? `${calIntegrations.length} connected`
        : 'Import from Google or your phone',
      action: () => setCalendarSyncOpen(true),
      iconColor: '#ffb74d',
    },
  ];

  const supportSection: SettingsItem[] = [
    { icon: 'explore', label: 'Discover family days out', sub: 'Nearby ideas for the whole family', action: () => history.push('/app/discover'), iconColor: '#aed581' },
    { icon: 'grid_view', label: 'All features', sub: 'Browse every surface', action: () => history.push('/app/features'), iconColor: '#c5512d' },
    { icon: 'help_outline', label: 'Help & Support', sub: 'FAQs and contact us', action: () => openExternal('mailto:hello@famhearth.com', '_blank'), iconColor: '#4fc3f7' },
    { icon: 'privacy_tip', label: 'Privacy Policy', sub: 'How we handle your data', action: () => history.push('/app/privacy'), iconColor: '#b39ddb' },
    { icon: 'description', label: 'Terms of Service', sub: 'Usage agreement', action: () => history.push('/app/terms'), iconColor: '#ff8a65' },
    { icon: 'info', label: 'About FamHearth', sub: 'Version and app info', action: () => history.push('/app/about'), iconColor: '#80cbc4' },
  ];

  const accountSection: SettingsItem[] = [
    { icon: 'logout', label: 'Sign Out', action: handleLogout, iconColor: '#ff8a65' },
  ];

  const renderSection = (title: string, items: SettingsItem[]) => (
    <div className="fh-settings-section">
      <p className="fh-settings-section-title">{title}</p>
      <div className="fh-settings-group">
        {items.map((item) => (
          <button
            key={item.label}
            onClick={() => item.action?.()}
            className={`fh-settings-item ${item.danger ? 'fh-settings-item--danger' : ''}`}
          >
            <div className="fh-settings-item__icon" style={{ background: `${item.iconColor}18` }}>
              <Icon name={item.icon} size={20} color={item.iconColor} weight="duotone" />
            </div>
            <div className="fh-settings-item__text">
              <span className="fh-settings-item__label">{item.label}</span>
              {item.sub && <span className="fh-settings-item__sub">{item.sub}</span>}
            </div>
            {!item.danger && <Icon name="chevron_right" size={16} color="rgba(255,255,255,0.15)" />}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <IonPage>
      <IonContent>
        {isDesktop && (
          <div className="fhv2-desktop" style={{ paddingBottom: 0 }}>
            <div className="fhv2-today-head">
              <div>
                <h1 className="fhv2-today-title">Settings <em>· {family?.name || 'your family'}</em></h1>
                <div className="fhv2-today-sub">
                  Preferences sync across all devices.
                </div>
              </div>
            </div>
          </div>
        )}
        <div className={`fhv2-settings-shell${isDesktop ? '' : ' is-mobile'}`}>
          {/* Desktop sub-nav (settings-desktop.pdf left rail). Scroll-to anchor
              links grouped under ACCOUNT / APP / ADMIN labels. */}
          {isDesktop && (
            <aside className="fhv2-settings-nav">
              <div className="fhv2-settings-nav-group">
                <div className="fhv2-settings-nav-label">Account</div>
                <a href="#sec-household" className="fhv2-settings-nav-link">Profile</a>
                <a href="#sec-household" className="fhv2-settings-nav-link">Household</a>
                <a href="#sec-household" className="fhv2-settings-nav-link">Members & roles</a>
              </div>
              <div className="fhv2-settings-nav-group">
                <div className="fhv2-settings-nav-label">App</div>
                <a href="#sec-app" className="fhv2-settings-nav-link">Notifications</a>
                <a href="#sec-app" className="fhv2-settings-nav-link">Privacy & data</a>
                <a href="#sec-app" className="fhv2-settings-nav-link">Customise home</a>
                <a href="#sec-app" className="fhv2-settings-nav-link">Appearance</a>
              </div>
              <div className="fhv2-settings-nav-group">
                <div className="fhv2-settings-nav-label">Admin</div>
                <a href="#sec-account" className="fhv2-settings-nav-link">Subscription</a>
                <a href="#sec-account" className="fhv2-settings-nav-link">Connected apps</a>
                <a href="#sec-account" className="fhv2-settings-nav-link">Export data</a>
                <a href="#sec-account" className="fhv2-settings-nav-link" style={{ color: 'var(--fhv2-terra)' }}>Delete account</a>
              </div>
            </aside>
          )}
          <div className={`fhv2-mpage fh-pb-safe${isDesktop ? ' fhv2-settings-desktop-wrap' : ''}`}>
          {!isDesktop && <FeatureMobileHeader title="Settings" />}

          {/* Profile card — terra background per prototype */}
          <button
            onClick={() => history.push('/app/profile')}
            className="fhv2-unset"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: 14, marginBottom: 16,
              background: 'rgba(197,81,45,0.1)', border: '1px solid rgba(197,81,45,0.25)',
              borderRadius: 14, cursor: 'pointer', textAlign: 'left',
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: user?.colour || 'var(--fhv2-terra)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, overflow: 'hidden' }}>
              {user?.avatar ? (
                <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                user ? initials(user.displayName || user.email) : '?'
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{user?.displayName || 'You'}</div>
              <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}{isAdmin ? ' · Admin' : ''}
              </div>
            </div>
            <Icon name="chevron_right" size={16} color="var(--fhv2-muted)" />
          </button>

          {/* Household group */}
          <div id="sec-household" className="fhv2-mlabel" style={{ marginTop: 4 }}>Household</div>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
            <button onClick={() => family ? setEditNameOpen(true) : history.push('/family-setup')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🏠</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Household details</div>
              <span style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>{family?.name || 'Set up'}</span>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => setMembersOpen(true)} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>👨‍👩‍👧‍👦</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Members ({members.length})</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => history.push('/app/customise-home')} className="fhv2-unset" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🏡</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Customise home</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
          </div>

          {/* App group */}
          <div id="sec-app" className="fhv2-mlabel">App</div>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
            <button onClick={() => setNotificationsOpen(true)} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🔔</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Notifications</div>
              <span style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>{Object.values(notifPrefs).filter(Boolean).length}/6</span>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => setIntegrationsOpen(true)} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🗓️</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Calendar integrations</div>
              <span style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>{calIntegrations.length ? `${calIntegrations.length} linked` : 'None'}</span>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => setCurrencyOpen(true)} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>💷</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Currency</div>
              <span style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>{activeCurrency.code}</span>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => history.push('/app/privacy')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🔐</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Privacy &amp; data</div>
              <span style={{ color: 'var(--fhv2-forest-light)', fontSize: 10, fontFamily: 'var(--fhv2-mono)' }}>E2E</span>
            </button>
            <button onClick={() => setAppearanceOpen(true)} className="fhv2-unset" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>🎨</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Appearance</div>
              <span style={{ color: 'var(--fhv2-muted)', fontSize: 12 }}>{themeLabel}</span>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
          </div>

          {/* Account group */}
          <div id="sec-account" className="fhv2-mlabel">Account</div>
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, overflow: 'hidden', marginBottom: 14 }}>
            <button onClick={() => history.push('/app/premium')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>⭐</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#fff' }}>Subscription</div>
                <div style={{ fontSize: 11, color: 'var(--fhv2-forest-light)' }}>Upgrade paths</div>
              </div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => history.push('/app/terms')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>📄</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Legal</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => openExternal('mailto:hello@famhearth.com', '_blank')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>💬</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>Help &amp; support</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            <button onClick={() => history.push('/app/about')} className="fhv2-unset" style={{ padding: 14, borderBottom: '1px solid var(--fhv2-line)', display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
              <span style={{ fontSize: 18 }}>ℹ️</span>
              <div style={{ flex: 1, fontSize: 13, color: '#fff' }}>About Famhearth</div>
              <Icon name="chevron_right" size={14} color="var(--fhv2-muted)" />
            </button>
            {/* Delete account — buried at the bottom of Account, no destructive emphasis */}
            <button
              onClick={() => { setDeleteConfirmText(''); setDeleteError(''); setDeleteAccountOpen(true); }}
              className="fhv2-unset"
              style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer', opacity: 0.7 }}
            >
              <span style={{ fontSize: 14, color: 'var(--fhv2-muted)' }}>·</span>
              <div style={{ flex: 1, fontSize: 12, color: 'var(--fhv2-muted)' }}>Delete account</div>
              <Icon name="chevron_right" size={12} color="var(--fhv2-muted)" />
            </button>
          </div>

          {/* Delete account confirmation modal */}
          <IonModal isOpen={deleteAccountOpen} onDidDismiss={() => { setDeleteAccountOpen(false); setDeleteConfirmText(''); setDeleteError(''); }} initialBreakpoint={0.6} breakpoints={[0, 0.6, 0.9]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4, color: 'var(--fhv2-terra)' }}>Delete account</h3>
              <p style={{ fontSize: 13, color: 'var(--fhv2-muted)', marginBottom: 18, lineHeight: 1.5 }}>
                This is permanent. Your profile, messages, lists, calendar events — everything you've put in Famhearth — will be erased. Your family will have to remove you from their household too.
              </p>
              <div style={{ background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--fhv2-terra)', marginBottom: 8 }}>Type <b>DELETE</b> to confirm:</div>
                <input
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(197,81,45,0.3)', color: '#fff', fontSize: 14, outline: 'none', fontFamily: 'var(--fhv2-mono)', letterSpacing: '2px' }}
                />
              </div>
              {deleteError && (
                <div style={{ background: 'rgba(197,81,45,0.1)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 10, padding: 10, fontSize: 12, color: 'var(--fhv2-terra)', marginBottom: 12 }}>
                  {deleteError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setDeleteAccountOpen(false); setDeleteConfirmText(''); setDeleteError(''); }} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                <button onClick={handleDeleteAccount} disabled={deleteConfirmText !== 'DELETE' || deleting} className="fhv2-btn" style={{ flex: 1, justifyContent: 'center', background: 'var(--fhv2-terra)', borderColor: 'var(--fhv2-terra)', color: '#fff', opacity: deleteConfirmText !== 'DELETE' || deleting ? 0.5 : 1 }}>
                  {deleting ? 'Deleting…' : 'Delete forever'}
                </button>
              </div>
            </div>
          </IonModal>

          {/* Notifications sheet */}
          <IonModal isOpen={notificationsOpen} onDidDismiss={() => setNotificationsOpen(false)} initialBreakpoint={0.8} breakpoints={[0, 0.8, 1]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4 }}>Notifications</h3>
              <p style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginBottom: 20 }}>Only what matters. Quiet hours always respected.</p>
              {[
                { key: 'morning', title: 'Morning brief', sub: 'Push at 7am with today\'s list. Quiet on weekends.' },
                { key: 'school', title: 'School alerts', sub: 'Uniform night-before, trip payments, school office emails.' },
                { key: 'geofence', title: 'Geofence arrivals', sub: '"Tayo arrived at school" pings.' },
                { key: 'budget', title: 'Budget alerts', sub: 'Ping when a category crosses 90%.' },
                { key: 'mentions', title: 'Chat · mentions only', sub: 'Otherwise deliver in a batch at 8pm.' },
                { key: 'quiet', title: 'Quiet hours', sub: '22:00 – 07:00 · no sound, no badge (emergencies still push).' },
              ].map((n, i, arr) => (
                <div key={n.key} style={{ padding: '14px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--fhv2-line)' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: '#fff' }}>{n.title}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--fhv2-muted)', marginTop: 3, lineHeight: 1.4 }}>{n.sub}</div>
                  </div>
                  <button onClick={() => toggleNotif(n.key)} className="fhv2-unset" style={{
                    width: 42, height: 24, borderRadius: 999, position: 'relative', cursor: 'pointer',
                    background: notifPrefs[n.key] ? 'var(--fhv2-forest)' : 'rgba(255,255,255,0.1)',
                    transition: 'background 0.2s',
                  }}>
                    <span style={{
                      position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      top: 3, left: notifPrefs[n.key] ? 21 : 3, transition: 'left 0.2s',
                    }} />
                  </button>
                </div>
              ))}
              <button onClick={() => setNotificationsOpen(false)} className="fhv2-btn is-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}>Done</button>
            </div>
          </IonModal>

          {/* Calendar integrations sheet */}
          <IonModal isOpen={integrationsOpen} onDidDismiss={() => setIntegrationsOpen(false)} initialBreakpoint={0.75} breakpoints={[0, 0.75, 1]}>
            <div style={{ background: 'var(--fhv2-ink)', padding: 24, minHeight: '100%', color: '#fff', fontFamily: 'var(--fhv2-sans)', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 26, marginBottom: 4 }}>Calendar integrations</h3>
              <p style={{ fontSize: 12, color: 'var(--fhv2-muted)', marginBottom: 20 }}>Pull shifts, school dates, and personal events into Famhearth.</p>

              {/* Google */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(100,181,246,0.18)', color: '#70a6db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📅</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Google Calendar</div>
                  <div style={{ fontSize: 11, color: googleIntegration ? 'var(--fhv2-forest-light)' : 'var(--fhv2-muted)', marginTop: 2 }}>
                    {googleIntegration ? `Connected · ${googleIntegration.account_label || 'account'}` : 'Sync shifts, events, birthdays'}
                  </div>
                </div>
                {googleIntegration ? (
                  <button onClick={() => handleDisconnect(googleIntegration, false)} className="fhv2-btn is-ghost" style={{ padding: '6px 10px', fontSize: 11 }}>Disconnect</button>
                ) : (
                  <button onClick={handleConnectGoogle} className="fhv2-btn is-primary" style={{ padding: '6px 12px', fontSize: 11 }}>Connect</button>
                )}
              </div>

              {/* Device (iOS / Android) */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(111,138,134,0.18)', color: 'var(--fhv2-sage)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📱</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Device calendar</div>
                  <div style={{ fontSize: 11, color: deviceIntegration ? 'var(--fhv2-forest-light)' : 'var(--fhv2-muted)', marginTop: 2 }}>
                    {deviceIntegration ? 'Connected · reads iOS/Android calendars' : 'Read events from this phone'}
                  </div>
                </div>
                {deviceIntegration ? (
                  <button onClick={() => handleDisconnect(deviceIntegration, false)} className="fhv2-btn is-ghost" style={{ padding: '6px 10px', fontSize: 11 }}>Disconnect</button>
                ) : (
                  <button onClick={handleImportDevice} className="fhv2-btn is-primary" style={{ padding: '6px 12px', fontSize: 11 }}>Connect</button>
                )}
              </div>

              {calIntegrations.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', textAlign: 'center', padding: 14 }}>
                  Connect at least one calendar to see all your events in one place.
                </div>
              )}
            </div>
          </IonModal>

          {/* Sign out */}
          <button onClick={handleLogout} className="fhv2-unset" style={{ display: 'block', width: '100%', textAlign: 'center', color: 'var(--fhv2-terra)', fontSize: 13, padding: '14px 0', cursor: 'pointer' }}>
            Sign out
          </button>

          <div style={{ textAlign: 'center', color: 'var(--fhv2-muted)', fontSize: 11, padding: '8px 0 20px', fontFamily: 'var(--fhv2-mono)' }}>
            FAMHEARTH · v2.4.1
          </div>

          {/* Legacy sections kept (hidden) so existing handlers/modal state stays wired */}
          <div style={{ display: 'none' }}>
            <button
              onClick={() => history.push('/app/profile')}
              className="fh-settings-profile"
            >
              <div
                className="fh-settings-profile__avatar"
                style={{ background: user?.colour || '#a43917' }}
              >
                {user ? initials(user.displayName || user.email) : '?'}
              </div>
              <div className="fh-settings-profile__info">
                <span className="fh-settings-profile__name">{user?.displayName || 'You'}</span>
                <span className="fh-settings-profile__email">{user?.email}</span>
              </div>
              <Icon name="chevron_right" size={18} color="var(--fh-dark-text-muted)" />
            </button>

            {/* Settings sections */}
            {renderSection('Family', familySection)}

            {/* Notifications — granular toggles */}
            <div className="fh-settings-section">
              <p className="fh-settings-section-title">Notifications</p>
              <div className="fh-settings-group">
                {[
                  { key: 'chat', label: 'Chat Messages', sub: 'New messages in family chat', icon: 'chat', color: '#4fc3f7' },
                  { key: 'bills', label: 'Bill Reminders', sub: 'Upcoming bill due dates', icon: 'receipt_long', color: '#ef9a9a' },
                  { key: 'tasks', label: 'Task Updates', sub: 'New tasks and completions', icon: 'check_circle', color: '#81c784' },
                  { key: 'calendar', label: 'Calendar Events', sub: 'Upcoming events and reminders', icon: 'event', color: '#ffb74d' },
                  { key: 'locator', label: 'Location Alerts', sub: 'Pick-me-up and safe zone alerts', icon: 'location_on', color: '#ce93d8' },
                  { key: 'special', label: 'Special Days', sub: 'Birthday and anniversary reminders', icon: 'cake', color: '#ff8a65' },
                ].map(pref => {
                  const enabled = localStorage.getItem(`fh-notif-${pref.key}`) !== 'off';
                  return (
                    <div key={pref.key} className="fh-settings-item" style={{ cursor: 'pointer' }} onClick={() => {
                      const next = !enabled;
                      localStorage.setItem(`fh-notif-${pref.key}`, next ? 'on' : 'off');
                      forceNotifUpdate(n => n + 1);
                    }}>
                      <div className="fh-settings-item__icon" style={{ background: `${pref.color}18` }}>
                        <Icon name={pref.icon} size={20} color={pref.color} weight="duotone" />
                      </div>
                      <div className="fh-settings-item__text">
                        <span className="fh-settings-item__label">{pref.label}</span>
                        <span className="fh-settings-item__sub">{pref.sub}</span>
                      </div>
                      <div style={{
                        width: 44, height: 26, borderRadius: 13, padding: 2,
                        background: enabled ? '#81c784' : 'rgba(255,255,255,0.12)',
                        transition: 'background 0.3s', flexShrink: 0,
                        position: 'relative',
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 2,
                          left: enabled ? 20 : 2,
                          transition: 'left 0.3s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {renderSection('Preferences', preferencesSection)}
            {renderSection('Integrations', integrationsSection)}
            {renderSection('Support', supportSection)}
            {renderSection('Account', accountSection)}

            {/* Delete account moved to Profile page for safety */}

            {/* Spacer at bottom */}
            <div style={{ height: 32 }} />
          </div>
        </div>
        </div>

        {/* ── Family Members Sheet ── */}
        <BottomSheet open={membersOpen} onClose={() => setMembersOpen(false)} title="Family Members">
          {members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: 'var(--fh-dark-text-secondary)', fontSize: 14, marginBottom: 16 }}>No family members yet</p>
              <button
                onClick={() => { setMembersOpen(false); history.push('/family-setup'); }}
                style={{
                  background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                  border: 'none', borderRadius: 12, padding: '12px 24px',
                  color: 'var(--fh-dark-text)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                }}
              >
                Set up family
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {members.map((member: any, i: number) => (
                <div key={member.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 12,
                  background: 'var(--fh-overlay-subtle)',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 700,
                    background: member.colour || MEMBER_COLOURS[i % MEMBER_COLOURS.length],
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}>
                    {member.avatar ? (
                      <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}  loading="lazy"/>
                    ) : (
                      initials(member.displayName)
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 600 }}>
                      {member.displayName}{member.id === user?.id ? ' (You)' : ''}
                    </div>
                    <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12, textTransform: 'capitalize' }}>
                      {member.role || 'Member'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BottomSheet>

        {/* ── Invite Code Sheet ── */}
        <BottomSheet open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Code">
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ color: 'var(--fh-dark-text-secondary)', fontSize: 13, marginBottom: 16 }}>
              Share this code with family members. They can enter it when signing up at famhearth.com
            </p>
            <div style={{
              background: 'var(--fh-overlay-light)', borderRadius: 12, padding: '20px',
              marginBottom: 16,
            }}>
              <div style={{
                fontSize: 32, fontWeight: 800, letterSpacing: '0.15em',
                color: 'var(--fh-primary-light)', fontFamily: '"Plus Jakarta Sans", sans-serif',
              }}>
                {family?.inviteCode || '---'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleCopyInvite}
                style={{
                  flex: 1, padding: '14px',
                  background: copied ? 'rgba(129,199,132,0.15)' : 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--fh-dark-border)', borderRadius: 50,
                  color: copied ? '#81c784' : '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Icon name={copied ? 'check' : 'content_copy'} size={16} color={copied ? '#81c784' : '#fff'} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleShareInvite}
                style={{
                  flex: 1, padding: '14px',
                  background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                  border: 'none', borderRadius: 50,
                  color: 'var(--fh-dark-text)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Icon name="share" size={16} color="#fff" />
                Share
              </button>
            </div>
          </div>
        </BottomSheet>

        {/* ── Edit Family Name Sheet ── */}
        <BottomSheet open={editNameOpen} onClose={() => setEditNameOpen(false)} title="Edit Family Name">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Family name"
              style={{
                width: '100%', padding: '14px 16px',
                background: 'var(--fh-overlay-subtle)',
                border: '1px solid var(--fh-dark-border)',
                borderRadius: 12, color: 'var(--fh-dark-text)',
                fontFamily: 'Manrope, sans-serif', fontSize: 15,
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#c5512d')}
              onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
            {nameError && <p style={{ color: '#ef5350', fontSize: 13, margin: 0 }}>{nameError}</p>}
            {nameSuccess && <p style={{ color: '#81c784', fontSize: 13, margin: 0 }}>Family name updated!</p>}
            <button
              onClick={handleSaveFamilyName}
              disabled={savingName || !familyName.trim()}
              style={{
                width: '100%', padding: '14px',
                background: savingName || !familyName.trim() ? 'rgba(164,57,23,0.4)' : 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                border: 'none', borderRadius: 50,
                color: 'var(--fh-dark-text)', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                opacity: savingName || !familyName.trim() ? 0.6 : 1,
              }}
            >
              {savingName ? 'Saving...' : 'Save'}
            </button>
          </div>
        </BottomSheet>

        {/* ── Appearance Sheet ── */}
        <BottomSheet open={appearanceOpen} onClose={() => setAppearanceOpen(false)} title="Appearance">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {([
              { key: 'dark' as const, label: 'Dark', desc: 'Easier on the eyes', icon: 'dark_mode' },
              { key: 'light' as const, label: 'Light', desc: 'Clean and bright', icon: 'light_mode' },
              { key: 'system' as const, label: 'System', desc: 'Match your device setting', icon: 'settings_brightness' },
            ]).map((theme) => {
              const isActive = themeMode === theme.key;
              return (
                <button
                  key={theme.key}
                  onClick={() => setThemeMode(theme.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', borderRadius: 12,
                    background: isActive ? 'rgba(164,57,23,0.12)' : 'rgba(255,255,255,0.04)',
                    border: isActive ? '1px solid rgba(164,57,23,0.3)' : '1px solid transparent',
                    cursor: 'pointer', width: '100%', textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? 'rgba(164,57,23,0.15)' : 'rgba(255,255,255,0.06)',
                  }}>
                    <Icon name={theme.icon} size={20} color={isActive ? '#c5512d' : 'rgba(255,255,255,0.5)'} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 600 }}>{theme.label}</div>
                    <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12 }}>{theme.desc}</div>
                  </div>
                  {isActive && <Icon name="check_circle" size={20} color="#c5512d" />}
                </button>
              );
            })}
          </div>
        </BottomSheet>

        {/* ── Currency Sheet ── */}
        <BottomSheet open={currencyOpen} onClose={() => { setCurrencyOpen(false); setCurrencySearch(''); }} title="Currency">
          <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12, marginBottom: 12 }}>
            Auto-detected from your device. Select to change.
          </p>
          <input
            type="text"
            value={currencySearch}
            onChange={e => setCurrencySearch(e.target.value)}
            placeholder="Search currencies..."
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 12, marginBottom: 12,
              background: 'var(--fh-overlay-light)', border: '1px solid var(--fh-dark-border)',
              color: 'var(--fh-dark-text)', fontSize: 14, fontFamily: 'Manrope, sans-serif', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
            {CURRENCIES.filter(c => {
              if (!currencySearch) return true;
              const q = currencySearch.toLowerCase();
              return c.code.toLowerCase().includes(q) || c.label.toLowerCase().includes(q) || c.symbol.includes(q);
            }).map((curr) => {
              const isActive = selectedCurrency === curr.code;
              return (
                <button key={curr.code} onClick={() => {
                  setSelectedCurrency(curr.code);
                  localStorage.setItem('fh-currency', curr.code);
                  localStorage.setItem('fh-budget-currency', curr.code);
                  api.put('/auth/preferences', { preferences: { currency: curr.code } })
                    .catch(err => console.warn('[MorePage] currency save failed:', err));
                  fetchExchangeRates();
                }} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderRadius: 12,
                  background: isActive ? 'rgba(164,57,23,0.12)' : 'rgba(255,255,255,0.04)',
                  border: isActive ? '1px solid rgba(164,57,23,0.3)' : '1px solid transparent',
                  cursor: 'pointer', width: '100%', textAlign: 'left',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'var(--fh-overlay-light)', color: 'var(--fh-dark-text)',
                      fontSize: 16, fontWeight: 700,
                    }}>{curr.symbol}</div>
                    <div>
                      <div style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 600 }}>{curr.label}</div>
                      <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12 }}>{curr.code}</div>
                    </div>
                  </div>
                  {isActive && <Icon name="check_circle" size={20} color="#c5512d" />}
                </button>
              );
            })}
          </div>
        </BottomSheet>

        {/* ── Connected Calendars Sheet ── */}
        <BottomSheet
          open={calendarSyncOpen}
          onClose={() => setCalendarSyncOpen(false)}
          title="Connected Calendars"
        >
          <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
            Import events from your phone or Google Calendar so everything shows up in one family view. Your family doesn't see your account — only the events you import.
          </p>

          {/* ── Google ── */}
          <div style={{
            background: 'var(--fh-overlay-subtle)', borderRadius: 14, padding: 14, marginBottom: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: googleIntegration ? 10 : 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 18, fontWeight: 800, color: '#4285F4',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
              }}>G</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 700 }}>Google Calendar</div>
                <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {googleIntegration
                    ? (googleIntegration.account_label || 'Connected')
                    : (googleConfigured ? 'Not connected' : 'Not configured on this server')}
                </div>
              </div>
            </div>

            {!googleIntegration && (
              <button
                onClick={handleConnectGoogle}
                disabled={!googleConfigured || calBusy === 'google-connect'}
                style={{
                  width: '100%', padding: '12px',
                  background: googleConfigured
                    ? 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))'
                    : 'rgba(255,255,255,0.08)',
                  border: 'none', borderRadius: 12,
                  color: '#fff', fontWeight: 700, fontSize: 14,
                  cursor: googleConfigured ? 'pointer' : 'not-allowed',
                  opacity: calBusy === 'google-connect' ? 0.6 : 1,
                }}
              >
                {calBusy === 'google-connect' ? 'Opening Google…' : 'Connect Google Calendar'}
              </button>
            )}

            {googleIntegration && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleSyncGoogle(googleIntegration)}
                  disabled={calBusy === `sync-${googleIntegration.id}`}
                  style={{
                    flex: 1, padding: '12px',
                    background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                    border: 'none', borderRadius: 12,
                    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    opacity: calBusy === `sync-${googleIntegration.id}` ? 0.6 : 1,
                  }}
                >
                  {calBusy === `sync-${googleIntegration.id}` ? 'Syncing…' : 'Sync now'}
                </button>
                <button
                  onClick={() => handleDisconnect(googleIntegration, false)}
                  disabled={calBusy === `disc-${googleIntegration.id}`}
                  style={{
                    flex: 1, padding: '12px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid var(--fh-dark-border)', borderRadius: 12,
                    color: 'var(--fh-dark-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}

            {googleIntegration?.last_synced_at && (
              <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 11, marginTop: 10, textAlign: 'center' }}>
                Last synced {new Date(googleIntegration.last_synced_at).toLocaleString()}
                {googleIntegration.last_sync_count != null && ` · ${googleIntegration.last_sync_count} events`}
              </div>
            )}
          </div>

          {/* ── Device (iOS / Android) ── */}
          {isDeviceCalendarAvailable() && (
            <div style={{
              background: 'var(--fh-overlay-subtle)', borderRadius: 14, padding: 14, marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon name={getDeviceCalendarPlatform() === 'ios' ? 'phone_iphone' : 'phone_android'} size={22} color="#ffb74d" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 700 }}>
                    {getDeviceCalendarPlatform() === 'ios' ? 'iPhone Calendar' : 'Android Calendar'}
                  </div>
                  <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12 }}>
                    Includes iCloud, Google, work and any other accounts set up on this device.
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleImportDevice}
                  disabled={calBusy === 'device-import'}
                  style={{
                    flex: 1, padding: '12px',
                    background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
                    border: 'none', borderRadius: 12,
                    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    opacity: calBusy === 'device-import' ? 0.6 : 1,
                  }}
                >
                  {calBusy === 'device-import'
                    ? 'Reading calendar…'
                    : deviceIntegration ? 'Sync from this phone' : 'Import from this phone'}
                </button>
                {deviceIntegration && (
                  <button
                    onClick={() => handleDisconnect(deviceIntegration, true)}
                    disabled={calBusy === `disc-${deviceIntegration.id}`}
                    style={{
                      flex: 1, padding: '12px',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid var(--fh-dark-border)', borderRadius: 12,
                      color: 'var(--fh-dark-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Remove imports
                  </button>
                )}
              </div>

              {deviceIntegration?.last_synced_at && (
                <div style={{ color: 'var(--fh-dark-text-muted)', fontSize: 11, marginTop: 10, textAlign: 'center' }}>
                  Last imported {new Date(deviceIntegration.last_synced_at).toLocaleString()}
                  {deviceIntegration.last_sync_count != null && ` · ${deviceIntegration.last_sync_count} events`}
                </div>
              )}
            </div>
          )}

          {!isDeviceCalendarAvailable() && (
            <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
              Open Famhearth on your iPhone or Android to import your device's calendar.
            </p>
          )}
        </BottomSheet>

        {/* ── Dashboard Features Sheet ── */}
        <BottomSheet open={dashboardOpen} onClose={() => setDashboardOpen(false)} title="Dashboard Features">
          <p style={{ color: 'var(--fh-dark-text-muted)', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
            Toggle features on or off to control what appears on your home screen.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
            {tiles.map(tile => (
              <button
                key={tile.id}
                onClick={() => toggleTile(tile.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 12,
                  background: tile.visible ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
                  border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: tile.visible ? `${tile.color}18` : 'rgba(255,255,255,0.04)',
                  flexShrink: 0,
                }}>
                  <Icon name={tile.icon} size={18} color={tile.visible ? tile.color : 'rgba(255,255,255,0.2)'} />
                </div>
                <span style={{
                  flex: 1, fontSize: 14, fontWeight: 600,
                  color: tile.visible ? '#fff' : 'rgba(255,255,255,0.35)',
                }}>
                  {tile.label}
                </span>
                <div style={{
                  width: 44, height: 26, borderRadius: 13, padding: 2,
                  background: tile.visible ? '#a43917' : 'rgba(255,255,255,0.1)',
                  transition: 'background 0.2s', position: 'relative',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: tile.visible ? 20 : 2,
                    transition: 'left 0.2s',
                  }} />
                </div>
              </button>
            ))}
          </div>
        </BottomSheet>

      </IonContent>
    </IonPage>
  );
};

export default MorePage;
